import {
  Body,
  Button,
  Heading,
  Icon,
  IconButton,
  Tooltip,
} from '@assembly-js/design-system';
import { Popover } from '@mui/material';
import clsx from 'clsx';
import { FC, useContext, useEffect, useState } from 'react';

import { CopyToClipboard } from 'src/components/CopyToClipboard';
import { UserRoles } from 'src/constants';
import { RouteContext } from 'src/context';
import { useFeatureFlags } from 'src/context/flagsContext';
import history from 'src/history';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { useAppSelector } from 'src/hooks/useStore';
import { useUpdateNotificationsMutedMutation } from 'src/services/api/clientsApi';
import {
  selectAllCompaniesMap,
  selectClientsMap,
} from 'src/store/clients/types';
import { selectCurrentUser } from 'src/store/storeUtils';
import DateUtils from 'src/utils/DateUtils';

type EntityType = 'client' | 'company';

const getCrmPath = (entityType: EntityType, entityId: string) =>
  entityType === 'client'
    ? `/clients/users/details/${entityId}`
    : `/companies/${entityId}`;

interface ContextBarAvatarPopoverProps {
  entityType: EntityType;
  entityId: string;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

export const ContextBarAvatarPopover: FC<ContextBarAvatarPopoverProps> = ({
  entityType,
  entityId,
  anchorEl,
  open,
  onClose,
}) => {
  const { pathname } = useContext(RouteContext);
  const isMobileScreen = useMediaQuery('sm', 'max');
  const companies = useAppSelector(selectAllCompaniesMap);
  const clients = useAppSelector(selectClientsMap);
  const { EnableClientNotificationsMute } = useFeatureFlags();
  const currentUser = useAppSelector(selectCurrentUser);
  const isAdminIU =
    currentUser?.fields?.roles?.includes(UserRoles.InternalAdmin) ?? false;
  const [updateNotificationsMuted] = useUpdateNotificationsMutedMutation();

  const client = entityType === 'client' ? clients.get(entityId) : undefined;
  const [isMuted, setIsMuted] = useState(
    client?.fields?.notificationsMuted ?? false,
  );

  // Sync local state when server state changes
  useEffect(() => {
    setIsMuted(client?.fields?.notificationsMuted ?? false);
  }, [client?.fields?.notificationsMuted]);

  const crmPath = getCrmPath(entityType, entityId);

  // Check if we're already on the entity's profile page
  const isOnProfilePage = pathname.startsWith(crmPath);

  const company =
    entityType === 'company' ? companies.get(entityId) : undefined;

  let entityName = '';
  let email = '';
  let createdDate = '';

  if (client) {
    entityName =
      `${client.fields.givenName ?? ''} ${client.fields.familyName ?? ''}`.trim();
    email = client.fields.email ?? '';
    ({ createdDate } = client);
  } else if (company) {
    entityName = company.fields.name;
    ({ createdDate } = company);
  }

  const handleOpenProfile = () => {
    history.push(crmPath);
    onClose();
  };

  const formattedDate = createdDate ? DateUtils.formatDate(createdDate) : '';

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        style: {
          transform: 'translateX(6px) translateY(4px)',
        },
      }}
      classes={{
        paper:
          'border border-solid border-secondary-hover shadow-dropdown-menu rounded-lg',
      }}
    >
      <div className="p-3 w-[220px]">
        <div className="flex items-center justify-between mb-[6px]">
          <Heading size="base" className="text-black-heading">
            {entityName}
          </Heading>
          {entityType === 'client' &&
            EnableClientNotificationsMute &&
            isAdminIU && (
              <Tooltip
                content={
                  <Body size="sm">
                    {isMuted
                      ? 'Unmute email notifications'
                      : 'Mute email notifications'}
                  </Body>
                }
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'center',
                  horizontal: 'right',
                }}
              >
                <IconButton
                  icon={isMuted ? 'bell-slash' : 'bell'}
                  variant="minimal"
                  size="sm"
                  label={
                    isMuted
                      ? 'Unmute email notifications'
                      : 'Mute email notifications'
                  }
                  className="text-secondary"
                  onClick={() => {
                    const newMuted = !isMuted;
                    setIsMuted(newMuted);
                    void updateNotificationsMuted({
                      id: entityId,
                      notificationsMuted: newMuted,
                      clientName: entityName,
                    });
                  }}
                />
              </Tooltip>
            )}
        </div>

        <div className="flex flex-col gap-1">
          {email && (
            <CopyToClipboard>
              {({ copy }) => (
                <button
                  className="w-full group cursor-pointer px-0 py-1 flex items-start gap-1.5 border-0 bg-transparent hover:bg-transparent relative"
                  onClick={() => copy(email)}
                >
                  <Icon
                    icon="Email"
                    className="w-3 h-3 text-secondary shrink-0 mt-1"
                  />
                  <Body
                    size="xs"
                    className={clsx(
                      'text-black-heading min-w-0 text-left line-clamp-3 break-all',
                      isMobileScreen && 'pr-5',
                    )}
                  >
                    {email}
                  </Body>
                  <div
                    className={clsx(
                      'absolute top-0.5 right-[-2px] w-6 h-6 flex items-center justify-center rounded hover:bg-secondary-hover bg-white',
                      isMobileScreen
                        ? 'block'
                        : 'opacity-0 group-hover:opacity-100 transition-opacity',
                    )}
                  >
                    <Icon icon="Copy" className="w-3 h-3 text-secondary" />
                  </div>
                </button>
              )}
            </CopyToClipboard>
          )}

          {formattedDate && (
            <div className="flex items-center gap-1.5">
              <Icon
                icon="Calendar"
                className="w-3 h-3 text-secondary shrink-0"
              />
              <Body size="xs" className="text-black-heading">
                Created {formattedDate}
              </Body>
            </div>
          )}
        </div>

        {!isOnProfilePage && (
          <Button
            className="w-full mt-2"
            onClick={handleOpenProfile}
            label="Open profile"
            variant="secondary"
            size="sm"
          />
        )}
      </div>
    </Popover>
  );
};
